import {FieldFunctionOptions, Reference, TypePolicies} from '@apollo/client';
import {ToReferenceFunction} from '@apollo/client/cache/core/types/common';
import gql from 'graphql-tag';

import {getPrefixToTypename} from 'graphql-client/APIIDPrefixToTypename';
import concatenatePaginatedQuery from 'graphql-client/concatenatePaginatedQuery';
import {
  getDirectStorableReference,
  getDirectStorableReferences,
  getGridInterfaceCacheId,
} from 'graphql-client/type-policies/GridInterfaceTypePolicy';
import {bioentityIdToTypename} from 'registry/graphql/GraphQLBioentityTypes';
import {getAPIIDPrefix} from 'util/APIUtil';
import ErrorSubclass from 'util/ErrorSubclass';

export type ExistingObjectReference = Reference | undefined;
export type TypenameGetterFn = (id: string) => string;
export type TypenameOptionalGetter = string | TypenameGetterFn;

export const UNIT_REDIRECT_FRAGMENT = gql`
  fragment UnitRedirectFragment on UnitBySymbolNode {
    symbol
    unit {
      id
    }
  }
`;

function copyIncomingUnitsToSymbolCache(incoming: Array<Reference>, options: FieldFunctionOptions): void {
  incoming.forEach((unitTypeRef: Reference) => {
    if (options.canRead(unitTypeRef)) {
      const unitRefs = options.readField('units', unitTypeRef);
      if (unitRefs) {
        (unitRefs as Array<Reference>).forEach((unitRef) => {
          if (options.canRead(unitRef)) {
            const id = options.readField('id', unitRef);
            const symbol = options.readField('symbol', unitRef);
            options.cache.writeFragment({
              fragment: UNIT_REDIRECT_FRAGMENT,
              data: {
                __typename: 'UnitBySymbolNode',
                symbol,
                unit: {__typename: 'UnitNode', id},
              },
            });
          }
        });
      }
    }
  });
}

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export function ignorePrevious(existing: Array<any> = [], incoming: Array<any>): Array<any> {
  return incoming;
}

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
function shallowMerge(existing: any, incoming: any): any {
  return {...existing, ...incoming};
}

function noCache(existing: unknown, incoming: unknown): undefined {
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  undefined;
}

export class EmptyArgumentsError extends ErrorSubclass {
  constructor(caller: string) {
    super(
      `${caller} is not meant to be called with empty arguments. ` +
        'This usually means that the calling type policy is unnecessary'
    );
  }
}

export function getReferenceById({
  id,
  typename,
  toReference,
}: {
  id?: string;
  typename: TypenameOptionalGetter;
  toReference: ToReferenceFunction;
}): Reference | undefined {
  if (typeof typename === 'function' && id == null) {
    throw new Error(
      'getReferenceById called with unresolvable typename. Did you forget to pass in query variables?'
    );
  }

  const type = typeof typename === 'string' ? typename : typename(id!);
  return id != null ? toReference({__typename: type, id}) : undefined;
}

function getReference(
  typename: TypenameOptionalGetter,
  {args, toReference}: FieldFunctionOptions,
  existing: ExistingObjectReference
): Reference | undefined {
  if (args == null) {
    throw new EmptyArgumentsError('getReference');
  }

  return existing !== undefined
    ? existing
    : getReferenceById({typename, id: args.id ?? args.input?.id, toReference});
}

export function getAllReferences(
  typename: TypenameOptionalGetter,
  {args, toReference, canRead}: FieldFunctionOptions,
  existing: ExistingObjectReference
): Reference | undefined {
  if (args == null) {
    throw new EmptyArgumentsError('getAllReferences');
  }
  if (existing !== undefined) {
    return existing;
  } else if (args.ids || args.inputs) {
    const refs = args.ids
      ? args.ids.map((id: string) => getReferenceById({typename, id, toReference}))
      : args.inputs.map((input: {id: string}) => getReferenceById({typename, id: input.id, toReference}));
    return refs.every((ref: Reference) => canRead(ref)) ? refs : undefined;
  } else {
    return undefined;
  }
}

function getTypenameFromId(id: string): string {
  return getPrefixToTypename()[getAPIIDPrefix(id)!];
}

/**
 * Type policies allow us to modify the read/write behavior of the Apollo Cache.
 *
 * We primarily use it as an optimization to help Apollo find existing data in our cache.
 *
 * Example:
 *
 *   // always makes network request
 *   `client.query({query: ALL_USERS_QUERY})`
 *
 *   // without a type policy, always makes a network request.
 *   // using a type policy, we can tell the cache to look for the user with id 1 before making this request
 *  `client.query({query: USER_BY_ID_QUERY, variables: { id: 1 }})`
 *
 * Additional notes on type policies:
 *
 *  1. undefined and dangling references are considered cache misses that will trigger a network call. null
 *     will not trigger a network call.
 *  2. getReferenceById and getAllReferences do not effectively lookup InaccessibleInfo nodes. Either dangling
 *  .  references or undefined is returned to trigger a cache lookup.
 *
 * For more detailed examples, see the tests in ApolloClient-test.rtl.tsx
 */
const typePolicies: TypePolicies = {
  Query: {
    fields: {
      _allNodes: {merge: ignorePrevious},
      analysis: (existing, options) => getReference('AnalysisNode', options, existing),
      analyses: (existing, options) => getAllReferences('AnalysisNode', options, existing),
      analyticsDashboard: (existing, options) => getReference('AnalyticsDashboardNode', options, existing),
      analyticsDashboards: (existing, options) =>
        getAllReferences('AnalyticsDashboardNode', options, existing),
      batch: (existing, options) => getReference('BatchNode', options, existing),
      batches: (existing, options) => getAllReferences('BatchNode', options, existing),
      bioentities: (existing, options) => getAllReferences(bioentityIdToTypename, options, existing),
      bioentity: (existing, options) => getReference(bioentityIdToTypename, options, existing),
      directBioentities: (existing, options) => getAllReferences(bioentityIdToTypename, options, existing),
      directBioentity: (existing, options) => getReference(bioentityIdToTypename, options, existing),
      boxes: (existing, options) => getAllReferences('BoxNode', options, existing),
      bulkAssembly: (existing, options) => getReference('BulkAssemblyNode', options, existing),
      bulkAssemblies: (existing, options) => getAllReferences('BulkAssemblyNode', options, existing),
      businessRule: (existing, options) => getReference('BusinessRuleNode', options, existing),
      businessRules: (existing, options) => getAllReferences('BusinessRuleNode', options, existing),
      businessRuleAssociation: (existing, options) =>
        getReference('BusinessRuleAssociationNode', options, existing),
      businessRuleAssociations: (existing, options) =>
        getAllReferences('BusinessRuleAssociationNode', options, existing),
      canvasFolderArtifacts: (existing, options) =>
        getAllReferences('CanvasFolderArtifactNode', options, existing),
      chart: (existing, options) => getReference('ChartNode', options, existing),
      charts: (existing, options) => getAllReferences('ChartNode', options, existing),
      collectibles: (existing, options) => getAllReferences(getTypenameFromId, options, existing),
      componentSequence: (existing, options) => getReference('ComponentSequenceNode', options, existing),
      componentSequencePrimer: (existing, options) =>
        getReference('ComponentSequencePrimerNode', options, existing),
      configImportHistoryEntry: (existing, options) =>
        getReference('ConfigImportHistoryEntryNode', options, existing),
      configImportHistoryEntries: (existing, options) =>
        getAllReferences('ConfigImportHistoryEntryNode', options, existing),
      connections: (existing, options) => getAllReferences('ConnectionNode', options, existing),
      container: (existing, options) => getReference('ContainerNode', options, existing),
      containers: (existing, options) => getAllReferences('ContainerNode', options, existing),
      currentTenant: {merge: shallowMerge},
      dataset: (existing, options) => getReference('DatasetNode', options, existing),
      datasets: (existing, options) => getAllReferences('DatasetNode', options, existing),
      entry: (existing, options) => getReference('EntryNode', options, existing),
      entries: (existing, options) => getAllReferences('EntryNode', options, existing),
      file: (existing, options) => getReference('FileNode', options, existing),
      files: (existing, options) => getAllReferences('FileNode', options, existing),
      folder: (existing, options) => getReference('FolderNode', options, existing),
      folders: (existing, options) => getAllReferences('FolderNode', options, existing),
      jellyfish: (existing, options) => getReference('JellyfishNode', options, existing),
      jellyfishes: (existing, options) => getAllReferences('JellyfishNode', options, existing),
      molecule: (existing, options) => getReference('MoleculeNode', options, existing),
      molecules: (existing, options) => getAllReferences('MoleculeNode', options, existing),
      party: (existing, options) => getReference(getTypenameFromId, options, existing),
      parties: (existing, options) => getAllReferences(getTypenameFromId, options, existing),
      plate: (existing, options) => getReference('PlateNode', options, existing),
      plates: (existing, options) => getAllReferences('PlateNode', options, existing),
      polymer: (existing, options) => getReference(bioentityIdToTypename, options, existing),
      polymers: (existing, options) => getAllReferences(bioentityIdToTypename, options, existing),
      procedureMethodDefinition: (existing, options) =>
        getReference('ProcedureMethodDefinitionNode', options, existing),
      procedureMethodDefinitions: (existing, options) =>
        getAllReferences('ProcedureMethodDefinitionNode', options, existing),
      procedureMethodExecutionInstance: (existing, options) =>
        getReference('ProcedureMethodExecutionInstanceNode', options, existing),
      procedureMethodExecutionInstances: (existing, options) =>
        getAllReferences('ProcedureMethodExecutionInstanceNode', options, existing),
      procedureRun: (existing, options) => getReference('ProcedureRunNode', options, existing),
      procedureRuns: (existing, options) => getAllReferences('ProcedureRunNode', options, existing),
      procedureStepDefinitionVersion: (existing, options) =>
        getReference('ProcedureStepDefinitionVersionNode', options, existing),
      procedureStepDefinitionVersions: (existing, options) =>
        getAllReferences('ProcedureStepDefinitionVersionNode', options, existing),
      procedureStepExecutionInstance: (existing, options) =>
        getReference('ProcedureStepExecutionInstanceNode', options, existing),
      procedureStepExecutionInstances: (existing, options) =>
        getReference('ProcedureStepExecutionInstanceNode', options, existing),
      process: (existing, options) => getReference('ProcessNode', options, existing),
      processes: (existing, options) => getAllReferences('ProcessNode', options, existing),
      processTasks: (existing, options) => getAllReferences('ProcessTaskNode', options, existing),
      processOutputs: (existing, options) => getAllReferences('ProcessOutputNode', options, existing),
      processTaskDefinitionsSupportingPlatformFeatureListing:
        concatenatePaginatedQuery('processTaskDefinitions'),
      project: (existing, options) => getReference('ProjectNode', options, existing),
      projects: (existing, options) => getAllReferences('ProjectNode', options, existing),
      // These six name the canonical typename rather than both, because they build a cache
      // reference and that resolves to exactly one entry. The protein* root fields are
      // deprecated but still served, so they redirect to the same entries as their aaSequence*
      // replacements (BNCH-169122).
      protein: (existing, options) => getReference('AaSequenceNode', options, existing),
      proteins: (existing, options) => getAllReferences('AaSequenceNode', options, existing),
      proteinAlignments: (existing, options) =>
        getAllReferences('AaSequenceAlignmentNode', options, existing),
      aaSequence: (existing, options) => getReference('AaSequenceNode', options, existing),
      aaSequences: (existing, options) => getAllReferences('AaSequenceNode', options, existing),
      aaSequenceAlignments: (existing, options) =>
        getAllReferences('AaSequenceAlignmentNode', options, existing),
      protocols: (existing, options) => getAllReferences('ProtocolNode', options, existing),
      publishingRecord: (existing, options) => getReference('PublishingRecordNode', options, existing),
      publishingRecords: (existing, options) => getAllReferences('PublishingRecordNode', options, existing),
      reaction: (existing, options) => getReference('ReactionNode', options, existing),
      reactions: (existing, options) => getAllReferences('ReactionNode', options, existing),
      request: (existing, options) => getReference('RequestNode', options, existing),
      requests: (existing, options) => getAllReferences('RequestNode', options, existing),
      requestV2Definition: (existing, options) => getReference('RequestV2DefinitionNode', options, existing),
      requestV2Definitions: (existing, options) =>
        getAllReferences('RequestV2DefinitionNode', options, existing),
      requestV2Submission: (existing, options) => getReference('RequestV2SubmissionNode', options, existing),
      requestV2Submissions: (existing, options) =>
        getAllReferences('RequestV2SubmissionNode', options, existing),
      run: (existing, options) => getReference('RunNode', options, existing),
      runs: (existing, options) => getAllReferences('RunNode', options, existing),
      schemaFieldSelector: (existing, options) => getReference('SchemaFieldSelectorNode', options, existing),
      schemaFieldSelectorOption: (existing, options) =>
        getReference('SchemaFieldSelectorOptionNode', options, existing),
      sequence: (existing, options) => getReference('SequenceNode', options, existing),
      sequences: (existing, options) => getAllReferences('SequenceNode', options, existing),
      sequenceAlignments: (existing, options) => getAllReferences('SequenceAnalysisNode', options, existing),
      stageEntries: (existing, options) => getAllReferences('StageEntryNode', options, existing),
      starredTagSchemas: {merge: ignorePrevious},
      storable: (existing, options) => getReference(getTypenameFromId, options, existing),
      storables: (existing, options) => getAllReferences(getTypenameFromId, options, existing),
      directStorable: (existing, options) => getDirectStorableReference(getTypenameFromId, options, existing),
      directStorables: (existing, options) =>
        getDirectStorableReferences(getTypenameFromId, options, existing),
      sampleType: (existing, options) => getReference('SampleTypeNode', options, existing),
      sampleTypes: (existing, options) => getAllReferences('SampleTypeNode', options, existing),
      studyableItems: (existing, options) => getAllReferences(getTypenameFromId, options, existing),
      chatSession: (existing, options) => getReference('ChatSessionNode', options, existing),
      chatSessionListing: (existing, options) => getAllReferences('ChatSessionNode', options, existing),
      studies: (existing, options) => getAllReferences('StudyNode', options, existing),
      tagSchema: (existing, options) => getReference('TagSchemaNode', options, existing),
      templateCollectionsByIds: (existing, options) =>
        getAllReferences('TemplateCollectionNode', options, existing),
      testDefinition: (existing, options) => getReference('TestDefinitionNode', options, existing),
      testDefinitions: (existing, options) => getAllReferences('TestDefinitionNode', options, existing),
      testOrder: (existing, options) => getReference('TestOrderNode', options, existing),
      testOrders: (existing, options) => getAllReferences('TestOrderNode', options, existing),
      trialSampleTestingStudyDesign: (existing, options) =>
        getReference('TrialSampleTestingStudyDesignNode', options, existing),
      trialSampleTestingStudyDesigns: (existing, options) =>
        getAllReferences('TrialSampleTestingStudyDesignNode', options, existing),
      unit: (existing, options) => getReference('UnitNode', options, existing),
      unitBySymbol: (existing, options) =>
        existing !== undefined
          ? existing
          : options.args != null
            ? options.toReference({__typename: 'UnitBySymbolNode', symbol: options.args.symbol})
            : undefined,
      unitType: (existing, options) => getReference('UnitTypeNode', options, existing),
      unitTypes: {
        merge: (existing, incoming, options) => {
          if (!existing) {
            copyIncomingUnitsToSymbolCache(incoming, options);
          }
          return incoming;
        },
      },
      validationRule: (existing, options) => getReference('ValidationRuleNode', options, existing),
      validationRules: (existing, options) => getAllReferences('ValidationRuleNode', options, existing),
      validationRuleAssociation: (existing, options) =>
        getReference('ValidationRuleAssociationNode', options, existing),
      validationRuleAssociations: (existing, options) =>
        getAllReferences('ValidationRuleAssociationNode', options, existing),
      workflows: (existing, options) => getAllReferences('WorkflowNode', options, existing),
      worklist: (existing, options) => getReference('WorklistNode', options, existing),
      worklists: (existing, options) => getAllReferences('WorklistNode', options, existing),
      worksheet: (existing, options) => getReference('WorksheetNode', options, existing),
      worksheets: (existing, options) => getAllReferences('WorksheetNode', options, existing),
    },
  },
  AnalyticsDashboardViewStateNode: {
    fields: {
      screenState: {merge: ignorePrevious},
    },
  },
  ArchiveDataNode: {
    merge: shallowMerge,
  },
  PartyPoliceableNode: {
    keyFields: ['party', ['id'], 'policeable', ['id']],
  },
  AnalyticsVisualizationScatterplotParameters: {
    fields: {
      outlierConfig: {merge: ignorePrevious},
    },
  },
  FormActionNode: {
    fields: {
      // FormActionNode doesn't have unique ids based on schema, so we just replace what's in the cache
      invocationParameters: {merge: ignorePrevious},
    },
  },
  RunNode: {
    fields: {
      linkedResults: concatenatePaginatedQuery('results'),
    },
  },
  RelevantTemplatesListingNode: {
    merge: ignorePrevious,
  },
  TenantNode: {
    fields: {
      organizations: {merge: ignorePrevious},
    },
  },
  ModifiedNucleotideNode: {
    keyFields: false,
  },
  LabelPrintingConfigNode: {
    fields: {
      bartenderServers: {merge: ignorePrevious},
    },
  },
  PermissionsNode: {
    merge: shallowMerge,
  },
  ProcessFlowchartTypeConfiguratorNode: {
    keyFields: ['flowchartTypeName'],
  },
  TemplateCollectiblesListingNode: {
    merge: noCache,
  },
  McpServerToolsResultNode: {
    merge: noCache,
  },
  SequenceVersionNode: {
    keyFields: ['dbId'],
  },
  StructureGraphNodeNode: {
    keyFields: false,
  },
  StructureGraphEdgeNode: {
    keyFields: false,
  },
  StructurePredictionRunNode: {
    keyFields: ['dbId'],
  },
  ProteinVersionNode: {
    keyFields: ['dbId'],
  },
  ProteinAnnotationVersionNode: {
    keyFields: ['dbId'],
  },
  // Replacement names for the two entries above (BNCH-169122). Both typenames exist in the schema
  // until the old ones are removed, and Apollo silently ignores a policy whose typename it does not
  // recognise — so without these the new types would normalise on a non-existent `id` with no
  // error. Removed alongside the ProteinNode entries.
  AaSequenceVersionNode: {
    keyFields: ['dbId'],
  },
  AaSequenceAnnotationVersionNode: {
    keyFields: ['dbId'],
  },
  GridInterface: {
    // This generates a custom cache id for GridInterfaces depending on the cell information loaded. We don't
    // use the usual list of keyFields here because we want a deterministic cache id that we also use in
    // getDirectStorableReference.
    keyFields: getGridInterfaceCacheId,
  },
  DilutionPlanNode: {
    merge: true,
  },
  UnitBySymbolNode: {
    keyFields: ['symbol'],
  },
  NameTemplatePartNode: {
    fields: {
      typeSpecificConfiguration: {
        merge: ignorePrevious,
      },
    },
  },
};

export default typePolicies;
